import { P } from "apparatus/library/common";
import { Snap } from "editing/snapping/type";
import { createEmptyGlobalSnapViz } from "layout/editor/global_snap_viz";
import { areRotationsClose } from "math";
import { Point } from "paper";
/** Allowed tolerance of rotation differences to allow a placeable to be snapped onto a surface. */
const PLACEABLE_SURFACE_ROTATION_TOLERANCE = 5.0;
/** Allowed tolerance in angle difference for 2 connectables to snap to each other. */
const CONNECTABLE_ROTATION_TOLERANCE = 15.0;
/**
 * Precomputes snapping information.
 *  Selected - components that are currently selected
 *  others - Other components, excluding the Selected ones, against which snap can occur.
 */
export function precomputeSnapping(selected, others) {
    var _a;
    var precomputed = [];
    // First categorise the currently selected apparatus by snap type.
    let placeables = [];
    let attachables = [];
    let connectables = [];
    let spottingTilePlaceable = [];
    let tlcSpots = [];
    let pluggables = [];
    let wires = [];
    let wirePorts = [];
    let shapeEnds = [];
    let bounds = [];
    for (let a of selected) {
        for (let snapping of a.globalSnapping) {
            if (snapping.type == "placeable") {
                placeables.push({ snapping: snapping, rotation: a.rotation + ((_a = snapping.facing) !== null && _a !== void 0 ? _a : 0) });
            }
            else if (snapping.type == "attachable") {
                attachables.push(snapping);
            }
            else if (snapping.type == "connectable") {
                // Modify the "facing" attribute to take into account the rotation.
                let adjustedFacing = (a.rotation + snapping.facing + 360) % 360;
                connectables.push(Object.assign(Object.assign({}, snapping), { facing: adjustedFacing }));
            }
            else if (snapping.type == "spotting_tile_placeable") {
                spottingTilePlaceable.push(Object.assign({}, snapping));
            }
            else if (snapping.type == "tlc_spot") {
                tlcSpots.push(snapping);
            }
            else if (snapping.type == "pluggable") {
                pluggables.push({ id: a.id, snapping });
            }
            else if (snapping.type == "wire") {
                wires.push(snapping);
            }
            else if (snapping.type == "wireport") {
                wirePorts.push(snapping);
            }
            else if (snapping.type == "shape_end") {
                shapeEnds.push(snapping);
            }
            else if (snapping.type == "bounds") {
                // For now, only allow snapping of objects aligned at 90 degrees.
                if (a.rotation % 90 == 0) {
                    bounds.push(snapping);
                }
            }
            a.setSnapVisualisation([
                "placeable", "attachable", "connectable", "spotting_tile_placeable",
                "tlc_spot", "pluggable", "wireport", "wire", "shape_end"
            ]);
        }
    }
    // Sort remaining items by type, depending on the snap types
    // of currently selected apparatus, e.g. find "placeable_surface"
    // for "placeable" items, but only if there are currently selected
    // items with snap type "placeable".
    for (let other of others) {
        let snapTypeMask = {};
        for (let snapping of other.globalSnapping) {
            if (placeables.length > 0 && snapping.type === "placeable_surface") {
                for (let placeable of placeables) {
                    // Rotation of the placeable surface.
                    const surfaceRotation = other.rotation + Snap.getFacing(snapping);
                    if (areRotationsClose(placeable.rotation, surfaceRotation, PLACEABLE_SURFACE_ROTATION_TOLERANCE)) {
                        let potentialSnapping = precomputePlaceable(placeable.snapping, snapping);
                        if (potentialSnapping) {
                            precomputed.push(potentialSnapping);
                            snapTypeMask.placeable_surface = true;
                        }
                    }
                }
            }
            if (attachables.length > 0 && snapping.type === "attachable_on") {
                for (let attachable of attachables) {
                    precomputed.push(precomputePointToLine(attachable, snapping));
                    snapTypeMask.attachable_on = true;
                }
            }
            if (connectables.length > 0 && snapping.type === "connectable") {
                // For connectables, evaluate them on the spot.
                let otherAngle = (other.rotation + snapping.facing + 540) % 360; // 540 = 360 + 180. Additioal 360 because modulo can return negative.
                for (let connectable of connectables) {
                    if (areRotationsClose(connectable.facing, otherAngle, CONNECTABLE_ROTATION_TOLERANCE)) {
                        precomputed.push({ type: "point_to_point", delta: snapping.at.subtract(connectable.at) });
                        snapTypeMask.connectable = true;
                    }
                }
            }
            if (spottingTilePlaceable.length > 0 && snapping.type === "spotting_tile") {
                for (let placeable of spottingTilePlaceable) {
                    const snaps = snapping.at.map(at => ({ type: "point_to_point", delta: at.subtract(placeable.at) }));
                    precomputed.push(...snaps);
                }
                snapTypeMask.spotting_tile = true;
            }
            if (tlcSpots.length > 0 && snapping.type === "tlc_plate") {
                for (let spot of tlcSpots) {
                    precomputed.push(precomputePointToLine(spot, snapping));
                }
                snapTypeMask.tlc_plate = true;
            }
            if (pluggables.length > 0 && snapping.type === "neck") {
                for (let pluggable of pluggables) {
                    // Check rotation of neck.
                    const neckRotation = other.rotation + snapping.facing;
                    precomputed.push({
                        type: "point_to_point",
                        delta: snapping.top.subtract(pluggable.snapping.at),
                        // Also set the appearance specs on the pluggable.
                        appearanceChange: {
                            id: pluggable.id,
                            partialSpecs: pluggable.snapping.requestDiameter(snapping.diameter),
                            rotation: neckRotation,
                        }
                    });
                    snapTypeMask.neck = true;
                }
            }
            // Snap wires and wireports to wires.
            if (wires.length > 0 && (snapping.type == "wire" || snapping.type == "wireport")) {
                for (let wire of wires) {
                    precomputed.push({ type: "point_to_point", delta: snapping.at.subtract(wire.at) });
                }
                snapTypeMask.wire = true;
                snapTypeMask.wireport = true;
            }
            // Snap wires to wireports.
            if (wirePorts.length > 0 && snapping.type == "wire") {
                snapTypeMask.wire = true;
                for (let wire of wirePorts) {
                    precomputed.push({ type: "point_to_point", delta: snapping.at.subtract(wire.at) });
                }
            }
            // Snap shape ends to other shape ends.
            if (shapeEnds.length > 0 && snapping.type == "shape_end") {
                snapTypeMask.shape_end = true;
                for (let shapeEnd of shapeEnds) {
                    precomputed.push({ type: "point_to_point", delta: snapping.at.subtract(shapeEnd.at) });
                }
            }
            // Bounds snap to other bounds in various ways. Only snap against others if they are
            // aligned at 90 degrees.
            if (bounds.length > 0 && snapping.type == "bounds" && (other.rotation % 90 == 0)) {
                snapTypeMask.bounds = true;
                for (let bound of bounds) {
                    // Ordering matters. Earlier means higher priority.
                    // Snap centers
                    precomputed.push({
                        type: "point_to_hline",
                        y: snapping.bounds.center.y - bound.bounds.center.y,
                        referencePoint: snapping.bounds.center,
                        snappedPoint: bound.bounds.center,
                    });
                    precomputed.push({
                        type: "point_to_vline",
                        x: snapping.bounds.center.x - bound.bounds.center.x,
                        referencePoint: snapping.bounds.center,
                        snappedPoint: bound.bounds.center,
                    });
                    // Snap top edge to top edge and bottom edge
                    precomputed.push({
                        type: "point_to_hline",
                        y: snapping.bounds.top - bound.bounds.top,
                        referencePoint: snapping.bounds.topCenter,
                        snappedPoint: bound.bounds.topCenter,
                    });
                    precomputed.push({
                        type: "point_to_hline",
                        y: snapping.bounds.bottom - bound.bounds.top,
                        referencePoint: snapping.bounds.bottomCenter,
                        snappedPoint: bound.bounds.topCenter
                    });
                    // Snap bottom edge to top edge and bottom edge
                    precomputed.push({
                        type: "point_to_hline",
                        y: snapping.bounds.top - bound.bounds.bottom,
                        referencePoint: snapping.bounds.topCenter,
                        snappedPoint: bound.bounds.bottomCenter,
                    });
                    precomputed.push({
                        type: "point_to_hline",
                        y: snapping.bounds.bottom - bound.bounds.bottom,
                        referencePoint: snapping.bounds.bottomCenter,
                        snappedPoint: bound.bounds.bottomCenter,
                    });
                    // Snap left edge to left edge and right edge
                    precomputed.push({
                        type: "point_to_vline",
                        x: snapping.bounds.left - bound.bounds.left,
                        referencePoint: snapping.bounds.leftCenter,
                        snappedPoint: bound.bounds.leftCenter,
                    });
                    precomputed.push({
                        type: "point_to_vline",
                        x: snapping.bounds.right - bound.bounds.left,
                        referencePoint: snapping.bounds.rightCenter,
                        snappedPoint: bound.bounds.leftCenter,
                    });
                    // Snap right edge to left edge and right edge
                    precomputed.push({
                        type: "point_to_vline",
                        x: snapping.bounds.left - bound.bounds.right,
                        referencePoint: snapping.bounds.leftCenter,
                        snappedPoint: bound.bounds.rightCenter,
                    });
                    precomputed.push({
                        type: "point_to_vline",
                        x: snapping.bounds.right - bound.bounds.right,
                        referencePoint: snapping.bounds.rightCenter,
                        snappedPoint: bound.bounds.rightCenter,
                    });
                }
            }
        }
        let snapTypeFilter = Snap.typeFilterFrom(snapTypeMask);
        if (snapTypeFilter.length > 0) {
            other.setSnapVisualisation(snapTypeFilter);
        }
    }
    return precomputed;
}
function precomputePlaceable(placeable, surface) {
    // Compute start and end positions in Move Space.
    let start = surface.start.subtract(placeable.at);
    let end = surface.end.subtract(placeable.at);
    let surfaceVector = end.subtract(start);
    return {
        type: "point_to_line",
        start: start,
        surfaceVector: surfaceVector,
        surfaceVectorNorm: surfaceVector.normalize()
    };
}
function precomputePointToLine(attachable, on) {
    // Compute start and end positions in Move Space.
    let start = on.start.subtract(attachable.at);
    let end = on.end.subtract(attachable.at);
    let surfaceVector = end.subtract(start);
    return {
        type: "point_to_line",
        start: start,
        surfaceVector: surfaceVector,
        surfaceVectorNorm: surfaceVector.normalize()
    };
}
/**
 * Evaluates an array of precomputed snappings and returns the vector it should snap to
 * or returns undefined if the point should not be snapped.
 */
export function evaluatePrecomputedSnappings(rawDeltaVector, snappings, snapDistance) {
    var _a;
    // Allow snapping on both vline and hline at the same time.
    let snap_hline;
    let snap_vline;
    let viz = createEmptyGlobalSnapViz();
    for (let snapping of snappings) {
        switch (snapping.type) {
            case "point_to_line": {
                let fromStartToDelta = rawDeltaVector.subtract(snapping.start);
                let project = fromStartToDelta.dot(snapping.surfaceVectorNorm);
                // Allow overhang by snapDistance.
                if (project < 0 && project > -snapDistance) {
                    project = 0;
                }
                else if (project > snapping.surfaceVector.length && project - snapDistance < snapping.surfaceVector.length) {
                    project = snapping.surfaceVector.length;
                }
                // Check the projection lies on the surface vector.
                if (project >= 0 && project <= snapping.surfaceVector.length) {
                    // Compute the nearest point on line.
                    let nearestPoint = snapping.start.add(snapping.surfaceVectorNorm.multiply(project));
                    // Measure the distance.
                    if (rawDeltaVector.isClose(nearestPoint, snapDistance)) {
                        return { delta: nearestPoint };
                    }
                }
                break;
            }
            case "point_to_point": {
                if (rawDeltaVector.isClose(snapping.delta, snapDistance)) {
                    return { delta: snapping.delta, appearanceChange: snapping.appearanceChange };
                }
                break;
            }
            case "point_to_hline": {
                if (snap_hline) {
                    break;
                }
                if (Math.abs(rawDeltaVector.y - snapping.y) < snapDistance) {
                    snap_hline = { delta: new Point(rawDeltaVector.x, snapping.y) };
                    // The end is not yet adjusted by delta. This will happen when returning.
                    viz.lines.push({
                        start: snapping.referencePoint,
                        end: snapping.snappedPoint
                    });
                }
                break;
            }
            case "point_to_vline": {
                if (snap_vline) {
                    break;
                }
                if (Math.abs(rawDeltaVector.x - snapping.x) < snapDistance) {
                    snap_vline = { delta: new Point(snapping.x, rawDeltaVector.y) };
                    // The end is not yet adjusted by delta. This will happen when returning.
                    viz.lines.push({
                        start: snapping.referencePoint,
                        end: snapping.snappedPoint
                    });
                }
                break;
            }
        }
        // Both vline and hline snap - combine them and return now.
        // We recompute the end here, because the delta has to be recomputed.
        if (snap_hline && snap_vline) {
            const combinedDelta = P(snap_vline.delta.x, snap_hline.delta.y);
            viz.lines[0].end = viz.lines[0].end.add(combinedDelta);
            viz.lines[1].end = viz.lines[1].end.add(combinedDelta);
            return { delta: combinedDelta, viz };
        }
    }
    if (snap_hline || snap_vline) {
        // We are guaranteed exactly one of above is set.
        const delta = (_a = snap_hline === null || snap_hline === void 0 ? void 0 : snap_hline.delta) !== null && _a !== void 0 ? _a : snap_vline === null || snap_vline === void 0 ? void 0 : snap_vline.delta;
        viz.lines[0].end = viz.lines[0].end.add(delta);
        return Object.assign(Object.assign({}, (snap_hline !== null && snap_hline !== void 0 ? snap_hline : snap_vline)), { viz });
    }
    return;
}
